import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { format } from 'date-fns';
import { SessionService, SessionsService } from '../../services';
import { Sidebar } from '../../components';
import TableSearch from '../../components/TableSearch';

const SessionsList = () => {
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pageCountShow, setPageCountShow] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pageCurrent, setPageCurrent] = useState(0);
  const pageLimit = 100;

  useEffect(() => {
    const getData = async () => {
      const data = await SessionsService.getAllSessions(pageCurrent, pageLimit, sortBy, sortOrder, search);

      if (data.status === 500) {
        SessionService.clearSession();
      }

      if (data.results) {
        setSessions(() => data.results);
        setPageCount(() => data.count);
        setPageCountShow(() => Math.ceil(data.count / pageLimit));
      }
    };

    getData();
  }, [pageCurrent, sortBy, sortOrder, search]);

  const handleSearch = async text => {
    setSearch(() => text);

    const data = await SessionsService.getAllSessions(pageCurrent, pageLimit, sortBy, sortOrder, search);

    if (data.status === 500) {
      SessionService.clearSession();
    }

    if (data.results) {
      setSessions(() => data.results);
      setPageCount(() => data.count);
      setPageCountShow(() => Math.ceil(data.count / pageLimit));
    }
  };

  const handlePageChange = async event => {
    const data = await SessionsService.getAllSessions(event.selected, pageLimit, sortBy, sortOrder, search);

    if (data.status === 500) {
      SessionService.clearSession();
    }

    if (data.results) {
      setSessions(() => data.results);
      setPageCount(() => data.count);
      setPageCountShow(() => Math.ceil(data.count / pageLimit));
      setPageCurrent(event.selected);
    }
  };

  const showAvatar = avatar => {
    return avatar ? avatar : '/avatar.jpg';
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 w-0 overflow-hidden">
        <main className="relative flex-1 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full px-4 sm:px-6 md:px-8">
              <div className="flex items-center mt-2 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                  />
                </svg>
                <h1 className="text-xl font-semibold">Sessions {pageCount > 0 && <span>({pageCount})</span>}</h1>
              </div>

              <TableSearch onChange={handleSearch} />

              <table className="w-full table-auto">
                <thead className="text-xs tracking-wide text-left bg-gray-100 border-b">
                  <tr>
                    <th className="p-2 text-left whitespace-nowrap">ID</th>
                    <th className="p-2 text-left whitespace-nowrap">Track</th>
                    <th className="p-2 text-left whitespace-nowrap">Vehicle</th>
                    <th className="p-2 text-left whitespace-nowrap">User</th>
                    <th className="p-2 text-left whitespace-nowrap">Laps</th>
                    <th className="p-2 text-left whitespace-nowrap">Started recording timestamp</th>
                    <th className="p-2 text-center whitespace-nowrap">Created</th>
                    <th className="p-2 text-center whitespace-nowrap">Updated</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-300">
                  {sessions.length > 0 ? (
                    sessions.map(session => (
                      <tr key={session._id}>
                        <td className="p-2 text-left whitespace-nowrap">
                          <Link to={session._id}>{session._id}</Link>
                        </td>
                        <td className="p-2 text-left whitespace-nowrap">
                          {session.track?.name}, {session.track?.country}
                        </td>
                        <td className="p-2 text-left whitespace-nowrap">
                          {session.vehicle?.brand} {session.vehicle?.model} {session.vehicle?.year}
                        </td>
                        <td className="p-2 text-left whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-2 sm:mr-3">
                              <Link to={`/users/${session.user?._id}`}>
                                <img
                                  className={`inline-block rounded-full ring-2 ring-gray-500 overflow-clip`}
                                  src={showAvatar(session.user?.avatar)}
                                  width="24"
                                  height="24"
                                  alt={session.user?.name}
                                />
                              </Link>
                            </div>
                            {session.user?.name ? (
                              <div className="font-medium text-gray-800">
                                <Link to={`/users/${session.user?._id}`}>{session.user?.name}</Link>
                              </div>
                            ) : (
                              '/'
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-left whitespace-nowrap">{session.laps.length}</td>
                        <td className="p-2 text-left whitespace-nowrap">{session.startedRecordingTimestamp}</td>
                        <td className="p-2 text-left whitespace-nowrap">{format(new Date(session.createdAt), 'dd/MM/yyyy')}</td>
                        <td className="p-2 text-right whitespace-nowrap">{format(new Date(session.updatedAt), 'dd/MM/yyyy')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-8 text-center text-gray-400" colSpan={13}>
                        No sessions in database
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <ReactPaginate
                breakLabel="..."
                onPageChange={handlePageChange}
                pageRangeDisplayed={pageCount}
                pageCount={pageCountShow}
                previousLabel="< prev"
                nextLabel="next >"
                className="flex items-center my-8"
                pageClassName="bg-gray-100 rounded-md m-1 text-sm font-semibold w-8 h-8 hover:bg-gray-200"
                pageLinkClassName="no-underline w-8 h-8 px-2 py-2 flex justify-center items-center"
                previousClassName="bg-gray-100 rounded-md m-1 text-sm font-semibold h-8 hover:bg-gray-200"
                previousLinkClassName="no-underline h-8 px-2 py-2 flex justify-center items-center"
                nextClassName="bg-gray-100 rounded-md m-1 text-sm font-semibold h-8 hover:bg-gray-200"
                nextLinkClassName="no-underline h-8 px-2 py-2 flex justify-center items-center"
                activeClassName="bg-black"
                activeLinkClassName="text-white"
                disabledClassName="bg-gray-50 hover:bg-gray-50"
                disabledLinkClassName="text-gray-400 hover:text-gray-400"
                renderOnZeroPageCount={null}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SessionsList;
